/**
 * Revelado temporal de un control: aparece con el mouse y se va solo.
 *
 * Está separado del componente porque lo que se puede romper son los
 * **temporizadores**, no el render: un apagado programado que sobrevive al
 * siguiente movimiento apaga el control con el mouse todavía encima. Eso se ve
 * como un parpadeo que nadie logra reproducir a mano.
 */

/** Un rectángulo en coordenadas de viewport — la forma de `DOMRect`. */
export interface Caja {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface Punto {
  x: number;
  y: number;
}

/** Si el punto cae dentro de la caja, bordes incluidos. */
export function estaDentro(caja: Caja, punto: Punto): boolean {
  return (
    punto.x >= caja.left && punto.x <= caja.right && punto.y >= caja.top && punto.y <= caja.bottom
  );
}

/**
 * Si el control tiene que revelarse por dónde quedó el puntero.
 *
 * `null` significa **que todavía no se movió el mouse**, y ahí no se revela
 * nada. No es una distinción teórica: el valor inicial obvio, `{x: 0, y: 0}`,
 * cae DENTRO del sidebar —su esquina superior izquierda es el origen del
 * viewport— y la pestaña aparecía sola al cargar la página, sin que nadie
 * hubiera tocado el mouse.
 */
export function debeRevelarse(cajas: readonly Caja[], punto: Punto | null): boolean {
  if (!punto) return false;
  return cajas.some((caja) => estaDentro(caja, punto));
}

export interface Revelador {
  /** Enciende y NO programa apagado: el puntero está sobre el control. */
  mostrar: () => void;
  /** Enciende y programa el apagado: el puntero está cerca, pero no encima. */
  mostrarYOcultar: () => void;
  /** Apaga en el acto. */
  ocultar: () => void;
  /** Descarta el apagado pendiente. Para el cleanup del efecto. */
  cancelar: () => void;
}

/**
 * Cada llamada **reprograma** el apagado en vez de sumar uno nuevo: sobre un
 * `mousemove` llegan decenas de eventos por segundo, y sin cancelar el anterior
 * el primero apagaría el control mientras el mouse sigue ahí.
 */
export function crearRevelador(alCambiar: (visible: boolean) => void, demoraMs: number): Revelador {
  let temporizador: ReturnType<typeof setTimeout> | null = null;

  const cancelar = () => {
    if (temporizador === null) return;
    clearTimeout(temporizador);
    temporizador = null;
  };

  const ocultar = () => {
    cancelar();
    alCambiar(false);
  };

  const mostrar = () => {
    cancelar();
    alCambiar(true);
  };

  return {
    mostrar,
    ocultar,
    cancelar,
    mostrarYOcultar: () => {
      mostrar();
      temporizador = setTimeout(ocultar, demoraMs);
    },
  };
}
